import { useState } from 'react';

import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageTemplate } from '@/modules/common';

import { CreateReportDialog } from '../components/create-report-dialog';
import { ReportCard } from '../components/report-card';
import { useReports } from '../hooks/use-reports';
import { ReportScope } from '../types';

const ReportGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
);

const SectionHeading = ({ title }: { title: string }) => (
  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
);

export const ReportsPage = () => {
  const { t } = useTranslation('authenticated.reports');
  const [createOpen, setCreateOpen] = useState(false);

  const { data: reports, isLoading } = useReports();

  const systemReports = reports?.filter(r => r.scope === ReportScope.SYSTEM) ?? [];
  const orgReports = reports?.filter(r => r.scope === ReportScope.ORG) ?? [];
  const privateReports = reports?.filter(r => r.scope === ReportScope.PRIVATE) ?? [];

  const hasAny = (reports?.length ?? 0) > 0;

  return (
    <PageTemplate
      title={t('page.title')}
      rightActions={
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('page.createReport')}
        </Button>
      }
    >
      {isLoading ? (
        <ReportGrid>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </ReportGrid>
      ) : !hasAny ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-lg font-medium">{t('page.noReports')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('page.noReportsDescription')}</p>
          <Button className="mt-4" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('page.createReport')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {systemReports.length > 0 && (
            <section className="flex flex-col gap-4">
              <SectionHeading title={t('page.systemSection')} />
              <ReportGrid>
                {systemReports.map(report => (
                  <ReportCard key={report.id} report={report} />
                ))}
              </ReportGrid>
            </section>
          )}

          {orgReports.length > 0 && (
            <section className="flex flex-col gap-4">
              <SectionHeading title={t('page.orgSection')} />
              <ReportGrid>
                {orgReports.map(report => (
                  <ReportCard key={report.id} report={report} />
                ))}
              </ReportGrid>
            </section>
          )}

          {privateReports.length > 0 && (
            <section className="flex flex-col gap-4">
              <SectionHeading title={t('page.privateSection')} />
              <ReportGrid>
                {privateReports.map(report => (
                  <ReportCard key={report.id} report={report} />
                ))}
              </ReportGrid>
            </section>
          )}
        </div>
      )}

      <CreateReportDialog open={createOpen} onOpenChange={setCreateOpen} />
    </PageTemplate>
  );
};
